import alert from "components/elements/Alert";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getAllowances, getMstAllowances, updateAllowances } from "../services/salaryApi";
import { SALARY_SLICE, setAllowancesType, setCurrentAllowances, setIsLoading } from "../slices/salarySlice";
import { message } from "antd";

function useEmployeeSalaryAllowance() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const { currentCompany } = useSelector((state) => state.company);
  const { allowancesType, currentAllowances, isLoading } = useSelector((state) => state?.[SALARY_SLICE]);
  const { t, i18n } = useTranslation();
  const refButton = useRef('button')
  const { id } = useParams();
  const [dataSource, setDataSource] = useState({})
  const [marriageStatus, setMarriageStatus] = useState('1001')

  const getAllowancesType = useCallback(
    async ({ year }) => {
      try {
        dispatch(setIsLoading(true));
        const response = await getMstAllowances({
          year: year,
          employeeId: id,
        });
        dispatch(setAllowancesType(response.data || []));
      } catch (error) {
        alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentEmployee, dispatch]
  );

  const getAllowancesBase = useCallback(
    async ({ year }) => {
      try {
        dispatch(setIsLoading(true));
        const response = await getAllowances({
          year: year,
          employeeId: id,
          companyId: currentCompany,
          actionId: currentEmployee,
        });
        dispatch(setCurrentAllowances(response.data));
      } catch (error) {
        alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentEmployee, currentCompany, dispatch]
  );

  const fetchAllowances = useCallback(async () => {
    await getAllowancesType({ year: moment().format('yyyy') })
    await getAllowancesBase({ year: moment().format('yyyy') })
  }, [getAllowancesBase, getAllowancesType])

  useEffect(() => {
    if (currentCompany) {
      fetchAllowances()
    }
  }, [])



  useEffect(() => {
    if (allowancesType) {
      let personalAllowance = {
        ...allowancesType?.personalAndFamily?.personalAllowance,
        ...allowancesType?.personalAndFamily?.marriageStatus?.find(o => o.code == marriageStatus),
        personalAllowance: allowancesType.personalAndFamily?.personalAllowance?.max
      }
      let parentalCareAllowance = {
        ...allowancesType?.personalAndFamily?.parentalCareAllowance,
        ...allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus),
        parentalCareAllowanceList: allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus).parentalCareAllowanceList?.map(o => {
          return {
            ...o,
            name: i18n.language == 'en' ? o.nameEn : o.name,
          }
        })
      }
      let healthInsurancePremiumForSpouse = {
        ...allowancesType?.insurance?.healthInsurancePremiumForSpouse,
        ...allowancesType?.insurance?.healthInsurancePremiumForSpouse?.marriageStatus?.map(o => {
          return {
            ...o,
            name: i18n.language == 'en' ? o.nameEn : o.name,
          }
        })?.find(o => o.marriageStatusCode == marriageStatus)
      }
      let marriageStatusList = allowancesType?.personalAndFamily?.marriageStatus?.map(o => {
        return {
          key: o.code,
          label: i18n.language == 'en' ? o.nameEn : o.name,
          value: o.code
        }
      })
      setDataSource({
        ...dataSource,
        personalAndFamily: {
          ...allowancesType?.personalAndFamily,
          personalAllowance,
          parentalCareAllowance,
          marriageStatus: marriageStatusList
        },
        other: {
          ...allowancesType?.other,
        },
        insurance: {
          ...allowancesType?.insurance,
          healthInsurancePremiumForSpouse
        }
      })

    }
  }, [allowancesType, marriageStatus, i18n.language])

  const onSubmit = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        let marriageStatus = values?.personalAndFamily?.personalAllowance?.code
        const modifiedValues = {
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          employeeAllowancesId: values?.employeeAllowancesId,
          year: moment().format('yyyy'),
          personalAndFamily: {
            marriageStatusCode: marriageStatus,
            parentalCareAllowancePermit: values?.parentalCareAllowancePermit && values?.parentalCareAllowanceList?.length > 0 ? "Y" : "N",
            parentalCareAllowanceList: values?.parentalCareAllowanceList,
            antenatalCareAndDeliveryExpensePermit: values?.antenatalCareandDeliveryExpensePermit && values?.antenatalCareAndDeliveryExpense != null ? "Y" : "N",
            antenatalCareAndDeliveryExpense: values?.antenatalCareAndDeliveryExpense,
            childAllowancePermit: values?.childAllowancePermit && values?.childAllowanceList?.length > 0 ? "Y" : "N",
            childAllowance: values?.childAllowanceList,
            disabledPersonOrIncompetentPersonSupportPermit: values?.disabledPersonorIncompetentPersonSupportPermit && values?.disabledPersonorIncompetentPersonSupport != null ? "Y" : "N",
            disabledPersonOrIncompetentPersonSupport: values?.disabledPersonorIncompetentPersonSupport,
          },
          insurance: {
            annuityInsurancePremiumPermit: values?.annuityInsurancePremiumPermit && values?.annuityInsurancePremium != null ? "Y" : "N",
            annuityInsurancePremium: values?.annuityInsurancePremium,
            personalHealthInsurancePremiumPermit: values?.personalHealthInsurancePremiumPermit && values?.personalHealthInsurancePremium != null ? "Y" : "N",
            personalHealthInsurancePremium: values?.personalHealthInsurancePremium,
            healthInsurancePremiumForParentsPermit: values?.healthInsurancePremiumForParentsPermit && values?.healthInsurancePremiumForParents != null ? "Y" : "N",
            healthInsurancePremiumForParents: values?.healthInsurancePremiumForParents,
            healthInsurancePremiumForSpousePermit: values?.healthInsurancePremiumForSpousePermit && values?.healthInsurancePremiumForSpouse != null ? "Y" : "N",
            healthInsurancePremiumForSpouse: values?.healthInsurancePremiumForSpouse,
          },
          other: {
            governmentPermanentEmployeeRegisteredProvidentFundPermit: values?.governmentPermanentEmployeeRegisteredProvidentFundPermit && values?.governmentPermanentEmployeeRegisteredProvidentFund != null ? "Y" : "N",
            governmentPermanentEmployeeRegisteredProvidentFund: values?.governmentPermanentEmployeeRegisteredProvidentFund,
            governmentPensionFundPermit: values?.governmentPensionFundPermit && values?.governmentPensionFund != null ? "Y" : "N",
            governmentPensionFund: values?.governmentPensionFund,
            privateTeacherAidFundContributionPermit: values?.privateTeacherAidFundContributionPermit && values?.privateTeacherAidFundContribution != null ? "Y" : "N",
            privateTeacherAidFundContribution: values?.privateTeacherAidFundContribution,
            retirementMutualFundPermit: values?.retirementMutualFundPermit && values?.retirementMutualFund != null ? "Y" : "N",
            retirementMutualFund: values?.retirementMutualFund,
            superSavingsFundUnitPurchasePermit: values?.superSavingsFundUnitPurchasePermit && values?.superSavingsFundUnitPurchase != null ? "Y" : "N",
            superSavingsFundUnitPurchase: values?.superSavingsFundUnitPurchase,
            nationalSavingsFundPermit: values?.nationalSavingsFundPermit && values?.nationalSavingsFund != null ? "Y" : "N",
            nationalSavingsFund: values?.nationalSavingsFund,
            thailandEnvironmentalSocialGovernanceFundPermit: values?.thailandEnvironmentalSocialGovernanceFundPermit && values?.thailandEnvironmentalSocialGovernanceFund != null ? "Y" : "N",
            thailandEnvironmentalSocialGovernanceFund: values?.thailandEnvironmentalSocialGovernanceFund,
            socialSecurityFundContributionPermit: values?.socialSecurityFundContributionPermit && values?.socialSecurityFundContribution != null ? "Y" : "N",
            socialSecurityFundContribution: values?.socialSecurityFundContribution,
            interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidencePermit: values?.interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidencePermit && values?.interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidence != null ? "Y" : "N",
            interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidence: values?.interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidence,
            socialEnterpriseInvestmentFundPermit: values?.socialEnterpriseInvestmentFundPermit && values?.socialEnterpriseInvestmentFund != null ? "Y" : "N",
            socialEnterpriseInvestmentFund: values?.socialEnterpriseInvestmentFund,
            donateToAPoliticalPartyPermit: values?.donateToAPoliticalPartyPermit && values?.donateToAPoliticalParty != null ? "Y" : "N",
            donateToAPoliticalParty: values?.donateToAPoliticalParty,
            educationalSupportDonationPermit: values?.educationalSupportDonationPermit && values?.educationalSupportDonation != null ? "Y" : "N",
            educationalSupportDonation: values?.educationalSupportDonation,
            generalDonationsPermit: values?.generalDonationsPermit && values?.generalDonations != null ? "Y" : "N",
            generalDonations: values?.generalDonations,
            easyEReceiptPermit: values?.easyEReceiptPermit && values?.easyEReceipt != null ? "Y" : "N",
            easyEReceipt: values?.easyEReceipt,
          },
        };
        await updateAllowances(modifiedValues);
        message.success(t("common.success"))
        let res = await refButton?.current?.resetForm()
        getAllowancesBase({ year: moment().format('yyyy') });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, marriageStatus, dispatch],
  );

  useEffect(() => {

    if (dataSource && currentAllowances) {


      let marriageStatus = currentAllowances?.personalAndFamily?.marriageStatus
      let marriageStatusList = allowancesType?.personalAndFamily?.marriageStatus?.map(o => {
        return {
          key: o.code,
          label: i18n.language == 'en' ? o.nameEn : o.name,
          value: o.code
        }
      })

      let parentalCareAllowance = {
        ...currentAllowances?.personalAndFamily?.parentalCareAllowance,
        ...allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus),
        parentalCareAllowanceList: allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus)?.parentalCareAllowanceList?.map(o => {
          return {
            ...o,
            name: i18n.language == 'en' ? o.nameEn : o.name,
          }
        })
      }

      let healthInsurancePremiumForSpouse = {
        ...currentAllowances?.insurance?.healthInsurancePremiumForSpouse,
        ...allowancesType?.insurance?.healthInsurancePremiumForSpouse?.marriageStatus?.map(o => {
          return {
            ...o,
            name: i18n.language == 'en' ? o.nameEn : o.name,
          }
        })?.find(o => o.marriageStatusCode == marriageStatus)
      }

      let personalAllowance = {
        ...currentAllowances?.personalAndFamily?.personalAllowance,
        ...allowancesType?.personalAndFamily?.marriageStatus?.find(o => o.code === marriageStatus),
        personalAllowance: allowancesType?.personalAndFamily?.personalAllowance?.max
      }


      setDataSource({
        // ...dataSource,
        employeeAllowancesId: currentAllowances?.id,
        // ...currentAllowances?.personalAndFamily,
        parentalCareAllowancePermit: currentAllowances?.personalAndFamily?.parentalCareAllowancePermit === "Y" ? true : false,
        parentalCareAllowanceList: currentAllowances?.personalAndFamily?.parentalCareAllowance?.parentalCareAllowanceList,
        antenatalCareandDeliveryExpensePermit: currentAllowances?.personalAndFamily?.antenatalCareAndDeliveryExpensePermit === "Y" ? true : false,
        childAllowancePermit: currentAllowances?.personalAndFamily?.childAllowancePermit === "Y" ? true : false,
        childAllowanceList: currentAllowances?.personalAndFamily?.childAllowance || dataSource?.personalAndFamily?.childAllowance?.childAllowanceList,
        disabledPersonorIncompetentPersonSupportPermit: currentAllowances?.personalAndFamily?.disabledPersonOrIncompetentPersonSupportPermit === "Y" ? true : false,
        disabledPersonorIncompetentPersonSupport: currentAllowances?.personalAndFamily?.disabledPersonOrIncompetentPersonSupport,


        personalAndFamily: {
          // ...currentAllowances?.personalAndFamily,
          ...allowancesType?.personalAndFamily,
          personalAllowance,
          parentalCareAllowance,
          marriageStatus: marriageStatusList,

        },

        ...currentAllowances?.insurance,
        annuityInsurancePremiumPermit: currentAllowances?.insurance?.annuityInsurancePremiumPermit === "Y" ? true : false,
        personalHealthInsurancePremiumPermit: currentAllowances?.insurance?.personalHealthInsurancePremiumPermit === "Y" ? true : false,
        healthInsurancePremiumForParentsPermit: currentAllowances?.insurance?.healthInsurancePremiumForParentsPermit === "Y" ? true : false,
        healthInsurancePremiumForSpousePermit: currentAllowances?.insurance?.healthInsurancePremiumForSpousePermit === "Y" ? true : false,

        insurance: {
          // ...currentAllowances?.insurance,
          ...allowancesType?.insurance,
          healthInsurancePremiumForSpouse
        },


        ...currentAllowances?.other,
        governmentPermanentEmployeeRegisteredProvidentFundPermit: currentAllowances?.other?.governmentPermanentEmployeeRegisteredProvidentFundPermit === "Y" ? true : false,
        governmentPensionFundPermit: currentAllowances?.other?.governmentPensionFundPermit === "Y" ? true : false,
        privateTeacherAidFundContributionPermit: currentAllowances?.other?.privateTeacherAidFundContributionPermit === "Y" ? true : false,
        retirementMutualFundPermit: currentAllowances?.other?.retirementMutualFundPermit === "Y" ? true : false,
        superSavingsFundUnitPurchasePermit: currentAllowances?.other?.superSavingsFundUnitPurchasePermit === "Y" ? true : false,
        nationalSavingsFundPermit: currentAllowances?.other?.nationalSavingsFundPermit === "Y" ? true : false,
        thailandEnvironmentalSocialGovernanceFundPermit: currentAllowances?.other?.thailandEnvironmentalSocialGovernanceFundPermit === "Y" ? true : false,
        socialSecurityFundContributionPermit: currentAllowances?.other?.socialSecurityFundContributionPermit === "Y" ? true : false,
        interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidencePermit: currentAllowances?.other?.interestPaidOnMortgageLoanForPurchaseOrConstructionOfResidencePermit === "Y" ? true : false,
        socialEnterpriseInvestmentFundPermit: currentAllowances?.other?.socialEnterpriseInvestmentFundPermit === "Y" ? true : false,
        donateToAPoliticalPartyPermit: currentAllowances?.other?.donateToAPoliticalPartyPermit === "Y" ? true : false,
        educationalSupportDonationPermit: currentAllowances?.other?.educationalSupportDonationPermit === "Y" ? true : false,
        generalDonationsPermit: currentAllowances?.other?.generalDonationsPermit === "Y" ? true : false,
        easyEReceiptPermit: currentAllowances?.other?.easyEReceiptPermit === "Y" ? true : false,
        antenatalCareAndDeliveryExpense: currentAllowances?.personalAndFamily?.antenatalCareAndDeliveryExpense,

      })
    }

  }, [currentAllowances])


  const onClear = useCallback(
    () => {
      if (allowancesType) {
        let personalAllowance = {
          ...allowancesType?.personalAndFamily?.personalAllowance,
          ...allowancesType?.personalAndFamily?.marriageStatus?.find(o => o.code == marriageStatus),
          personalAllowance: allowancesType.personalAndFamily?.personalAllowance?.max
        }
        let parentalCareAllowance = {
          ...allowancesType?.personalAndFamily?.parentalCareAllowance,
          ...allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus),
          parentalCareAllowanceList: allowancesType?.personalAndFamily?.parentalCareAllowance?.marriageStatus?.find(o => o.marriageStatusCode == marriageStatus).parentalCareAllowanceList?.map(o => {
            return {
              ...o,
              name: i18n.language == 'en' ? o.nameEn : o.name,
            }
          })
        }
        let healthInsurancePremiumForSpouse = {
          ...allowancesType?.insurance?.healthInsurancePremiumForSpouse,
          ...allowancesType?.insurance?.healthInsurancePremiumForSpouse?.marriageStatus?.map(o => {
            return {
              ...o,
              name: i18n.language == 'en' ? o.nameEn : o.name,
            }
          })?.find(o => o.marriageStatusCode == marriageStatus)
        }
        let marriageStatusList = allowancesType?.personalAndFamily?.marriageStatus?.map(o => {
          return {
            key: o.code,
            label: i18n.language == 'en' ? o.nameEn : o.name,
            value: o.code
          }
        })
        setDataSource({
          employeeAllowancesId: dataSource?.employeeAllowancesId,

          personalAndFamily: {
            ...allowancesType?.personalAndFamily,
            personalAllowance,
            parentalCareAllowance,
            marriageStatus: marriageStatusList
          },
          other: {
            ...allowancesType?.other,
          },
          insurance: {
            ...allowancesType?.insurance,
            healthInsurancePremiumForSpouse
          }
        })
      }
    },
    [allowancesType, dataSource, i18n.language, marriageStatus],
  )

  return {
    dataSource,
    setMarriageStatus,
    setDataSource,
    onSubmit,
    onClear,
    refButton,
    isLoading
  };
}

export default useEmployeeSalaryAllowance;
